<template>
  <div class="s-strip s-leasing-car">
    <div class="page-container">
      <div class="row gx-5">
        <div class="col-lg-8">
          <div class="s-leasing-car__header mb-20">
            <h1 class="h1 extrabold mb-5">{{ make }} {{ model }}</h1>
            <h2 class="h4">{{ modelDescription }}</h2>
          </div>

          <div class="s-leasing-car__gallery mb-32">
            <div v-if="highlightText" class="s-leasing-car__tagged">
              <span>{{ highlightText }}</span>
            </div>
            <VehicleGallerySlick
              v-if="recordsGallery.length"
              :records="recordsGallery" />
            <div
              v-if="recordsGallery.length"
              class="d-flex justify-content-end">
              <small>
                <i>{{ dictionary.galleryDisclaimer }}</i>
              </small>
            </div>
          </div>

          <VehicleTechs
            v-if="hasSpecs"
            :item="carSpecs"
            class="d-none d-lg-block" />

          <Accordion
            :title="dictionary.techTitle"
            class="d-block d-lg-none mb-80">
            <VehicleTechs v-if="hasSpecs" :item="carSpecs" :hide-title="true" />
          </Accordion>

          <div class="d-block d-lg-none mb-80">
            <LeasingCarAside id-prefix="devices" />
          </div>

          <section v-if="leasingTerms" class="s-contact-note mb-80">
            <div class="s-contact-note__body">
              <div class="s-contact-note__details">
                <div class="s-contact-note__picture">
                  <IdeaLottie width="20"></IdeaLottie>
                </div>

                <div>
                  <div
                    class="s-contact-note__description"
                    v-html="leasingTerms"></div>
                </div>
              </div>
            </div>
          </section>

          <VehicleBenefits
            :records="characteristics"
            :columns="2"
            class="d-none d-lg-block" />

          <Accordion
            :title="dictionary.benefitsTitle"
            class="d-block d-lg-none">
            <VehicleBenefits
              :records="characteristics"
              :columns="2"
              :hide-title="true" />
          </Accordion>
        </div>

        <div class="col-lg-4 d-none d-lg-block">
          <LeasingCarAside id-prefix="desktop" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import VehicleGallerySlick from './../components/leaseitnow/VehicleGallerySlick.vue'
import IdeaLottie from './../components/lotties/Idea.vue'
/* eslint-disable no-unused-vars */
import { difference, last, remove, sum, uniqBy } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import VehicleTechs from './../components/leasingOffers/VehicleTechs.vue'
import VehicleBenefits from './../components/VehicleBenefits.vue'
import LeasingCarAside from './../components/LeasingCarAside.vue'
import Accordion from './../components/common/Accordion.vue'

export default {
  components: {
    VehicleGallerySlick,
    VehicleTechs,
    VehicleBenefits,
    IdeaLottie,
    LeasingCarAside,
    Accordion,
  },
  data() {
    return {
      key: uuidv4(),
      isLoading: false,
      errorMsgs: [],
      // data sources
      make: '',
      model: '',
      modelDescription: '',
      highlightBgColor: '',
      highlightText: '',

      carSpecs: {
        transmissionType: '',
        fuel: '',
        consumption: '',
        energyConsumption: '',
        autonomy: '',
        emissions: '',
      },

      pricePerMonthVatIncluded: '',
      priceVatIncludedWithoutPrepayment: '',
      pricePerMonthVatExcluded: '',
      priceVatExcludedWithoutPrepayment: '',

      retailPrice: '',
      showAlwaysRetailPriceBeforeTaxes: false,
      retailPriceBeforeTaxes: '',
      buyingPrice: '',
      prepayment: '',
      prepaymentVatIncluded: '',
      paymentInAdvance: '',

      galleryImages: [],
      characteristics: [],
      characteristicsText: null,
      vehicleBenefits: [],
      rentalDuration: '',

      withPrepayment: false,
      vatIncluded: false,
      sponsorsText: null,
      leasingTerms: '',
      bookingUrl: '',
    }
  },
  computed: {
    showRetailPriceBeforeTaxes() {
      if (
        this.showAlwaysRetailPriceBeforeTaxes ||
        (this.retailPriceBeforeTaxes !== '' && this.buyingPrice === '')
      ) {
        return this.retailPriceBeforeTaxes
      }
      return false
    },

    hasSpecs() {
      return Object.entries(this.carSpecs).find((x) => x[1] !== '')
    },

    calculatedPricePerMonth() {
      if (this.withPrepayment) {
        return this.pricePerMonthVatExcluded
      }
      return this.priceVatExcludedWithoutPrepayment
    },

    calculatedPricePerMonthIncldVat() {
      if (this.withPrepayment) {
        return this.pricePerMonthVatIncluded
      }
      return this.priceVatIncludedWithoutPrepayment
    },

    recordsGallery() {
      if (!this.galleryImages) {
        return []
      }
      return this.galleryImages.map((x) => ({
        id: uuidv4(),
        thumbnail: { url: x.url, alt: '' },
        image: { url: x.url, alt: '' },
      }))
    },
  },
  mounted() {
    const {
      make,
      model,
      modelDescription,
      highlightBgColor,
      highlightText,
      transmission,
      fuel,
      consumption,
      energyConsumption,
      autonomy,
      emissions,
      pricePerMonthVatIncluded,
      priceVatIncludedWithoutPrepayment,
      pricePerMonthVatExcluded,
      priceVatExcludedWithoutPrepayment,
      retailPrice,
      showAlwaysRetailPriceBeforeTaxes,
      retailPriceBeforeTaxes,
      buyingPrice,
      prepayment,
      prepaymentVatIncluded,
      paymentInAdvance,

      rentalDuration,
      sponsorsText,
      galleryImages,
      characteristics,
      characteristicsText,
      leasingTerms,
      bookingUrl,
    } = lib.getData('leasingCarData')
    this.make = make
    this.model = model
    this.modelDescription = modelDescription
    this.highlightBgColor = highlightBgColor
    this.highlightText = highlightText

    this.carSpecs.transmissionType = transmission
    this.carSpecs.fuel = fuel
    this.carSpecs.consumption = consumption
    this.carSpecs.energyConsumption = energyConsumption
    this.carSpecs.autonomy = autonomy
    this.carSpecs.emissions = emissions

    this.pricePerMonthVatIncluded = pricePerMonthVatIncluded
    this.priceVatIncludedWithoutPrepayment = priceVatIncludedWithoutPrepayment
    this.pricePerMonthVatExcluded = pricePerMonthVatExcluded
    this.priceVatExcludedWithoutPrepayment = priceVatExcludedWithoutPrepayment
    this.retailPrice = retailPrice
    this.showAlwaysRetailPriceBeforeTaxes = showAlwaysRetailPriceBeforeTaxes
    this.retailPriceBeforeTaxes = retailPriceBeforeTaxes
    this.buyingPrice = buyingPrice
    this.prepayment = prepayment
    this.prepaymentVatIncluded = prepaymentVatIncluded
    this.paymentInAdvance = paymentInAdvance

    this.galleryImages = galleryImages

    this.characteristics = characteristics.map((x) => {
      return { summary: x }
    })

    this.characteristicsText = characteristicsText

    this.sponsorsText = sponsorsText
    this.rentalDuration = rentalDuration

    this.leasingTerms = leasingTerms

    this.bookingUrl = bookingUrl
  },
}
</script>
<style lang="scss" scoped>
.s-leasing-car__tagged {
  background-color: v-bind(highlightBgColor);
  color: white;
}

.s-contact-note__body {
  width: 100%;
}
</style>
